import { useEffect, useMemo, useState } from 'react'
import { useAgentPreferences } from '@/hooks/useAgentPreferences'
import { useTranslations } from '@/hooks/useTranslations'
import { normalizeTitle } from '@/utils/normalizeTitle'
import { Switch } from '../ui/Switch'

export interface AgentPreferenceItem {
  agentName: string
  tools: Record<string, boolean>
}

export interface AgentPreferencesPanelProps {
  preferences: AgentPreferenceItem[]
  disabled?: boolean
}

const toDraftMap = (items: AgentPreferenceItem[]) =>
  items.reduce<Record<string, Record<string, boolean>>>((acc, item) => {
    acc[item.agentName] = { ...item.tools }
    return acc
  }, {})

const AgentPreferencesPanel = ({ preferences, disabled }: AgentPreferencesPanelProps) => {
  const t = useTranslations()
  const { upsertSingle } = useAgentPreferences()
  const [draftByAgent, setDraftByAgent] = useState<Record<string, Record<string, boolean>>>(() =>
    toDraftMap(preferences)
  )
  const [pendingByAgent, setPendingByAgent] = useState<Record<string, Record<string, boolean>>>({})

  useEffect(() => {
    setDraftByAgent(toDraftMap(preferences))
  }, [preferences])

  const errorMessage = upsertSingle.error?.message

  const hasAnyTools = useMemo(() => preferences.some((item) => Object.keys(item.tools).length > 0), [preferences])

  const toggleTool = (agentName: string, toolKey: string, nextValue: boolean) => {
    setDraftByAgent((prev) => {
      const currentForAgent = prev[agentName] ?? {}
      const previousForAgent = currentForAgent
      const nextForAgent = {
        ...currentForAgent,
        [toolKey]: nextValue
      }

      // Optimistically update local draft
      const nextMap = {
        ...prev,
        [agentName]: nextForAgent
      }

      // Mark only this specific switch as pending
      setPendingByAgent((pendingPrev) => ({
        ...pendingPrev,
        [agentName]: {
          ...(pendingPrev[agentName] ?? {}),
          [toolKey]: true
        }
      }))

      // Auto-save per agent; clear pending for this switch when done
      upsertSingle.mutate(
        {
          agentName,
          input: { tools: nextForAgent }
        },
        {
          onError: () => {
            // Revert this specific agent's tools on error
            setDraftByAgent((revertPrev) => ({
              ...revertPrev,
              [agentName]: previousForAgent
            }))
          },
          onSettled: () => {
            setPendingByAgent((pendingPrev) => {
              const agentPending = { ...(pendingPrev[agentName] ?? {}) }
              delete agentPending[toolKey]
              if (Object.keys(agentPending).length === 0) {
                const { [agentName]: _removed, ...rest } = pendingPrev
                return rest
              }
              return {
                ...pendingPrev,
                [agentName]: agentPending
              }
            })
          }
        }
      )

      return nextMap
    })
  }

  return (
    <div className='mp-agent-preferences-panel'>
      {!hasAnyTools && <div className='mp-agent-preferences-empty'>{t.agentSettingsNoConfigurableTools}</div>}

      {preferences.map((item) => {
        const tools = draftByAgent[item.agentName] ?? {}
        const toolEntries = Object.entries(tools)

        return (
          <div key={item.agentName} className='mp-agent-preferences-agent'>
            <div className='mp-agent-preferences-agent-header'>
              <strong>{item.agentName}</strong>
            </div>

            {toolEntries.length === 0 ? (
              <div className='mp-agent-preferences-empty'>{t.agentSettingsNoToolsForAgent}</div>
            ) : (
              <div className='mp-agent-preferences-tools'>
                {toolEntries.map(([toolKey, enabled]) => {
                  const isPending = Boolean(pendingByAgent[item.agentName]?.[toolKey])
                  return (
                    <label key={`${item.agentName}:${toolKey}`} className='mp-agent-preferences-tool'>
                      <span className='mp-agent-preferences-tool-label'>{normalizeTitle(toolKey)}</span>
                      <Switch
                        checked={enabled}
                        onChange={(next) => toggleTool(item.agentName, toolKey, next)}
                        disabled={Boolean(disabled) || isPending}
                      />
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {errorMessage && <div className='mp-agent-preferences-error'>{errorMessage}</div>}
    </div>
  )
}

export default AgentPreferencesPanel
